import { ExclamationCircleOutlined, EditOutlined, PlusOutlined, FileSearchOutlined } from '@ant-design/icons'
import { Button, Table, Typography, Tooltip } from 'antd'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useMemo, useEffect, useCallback } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { Link, useLocation } from 'react-router-dom'
import { formatDOB } from '../../lib/datetime'
import { reportShowPath, reportEditPath } from '../../lib/reports'
import { PAGE_SIZE_OPTIONS, PAGE_SIZE } from '../../lib/constants'
import {
  useIsAcknowledged,
  useReports,
  useReportsPagination,
} from '../../redux/slices/reports'
import {
  useCanSign,
  useFeatureEnabled,
  useIsAdmin,
} from '../../redux/slices/session'
import useQueryParams from '../../hooks/useQueryParams'
import useLocalStorage from '../../hooks/useLocalStorage'
import DiseasesTags from '../Common/DiseasesTags'
import DataExtractionIcon from '../Common/DataExtractionIcon'
import DataExtractionReference from '../Common/DataExtractionReference'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import Acknowledge from './Acknowledge'
import PatientEntry from '../Common/PatientEntry'

const { Text } = Typography

const useStyles = createUseStyles({
  criticalIcon: {
    color: 'red',
    paddingTop: '0.3em',
  },
  parsingReference: {
    padding: '1rem 0',
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  inactiveRow: {
    '& td': { color: '#999 !important', fontStyle: 'italic' },
    opacity: 0.6,
  },
})

const BASE_COLUMNS = [
  {
    title: 'MRN',
    dataIndex: ['patient', 'practiceMrn'],
    key: 'MRN',
  },
  {
    title: 'DOB',
    dataIndex: ['patient', 'birthDate'],
    key: 'dob',
    render: formatDOB,
  },
  {
    title: 'Stroke Prevention',
    key: 'diseases',
    render(text, report) {
      return <DiseasesTags patientSummary={report.patient} />
    },
  },
  {
    title: 'Device Type',
    dataIndex: ['device', 'deviceModel', 'deviceType', 'name'],
    key: 'deviceType',
  },
  {
    title: 'ICM Active',
    key: 'icmActive',
    align: 'center',
    render(text, report) {
      const { device } = report
      if (!device?.icmCapable) return '—'
      return <CircleCheckIcon checked={device.icmActive} />
    },
  },
  {
    title: 'CPT Code',
    dataIndex: 'cptCode',
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: 'icd10Code',
    key: 'icd10Code',
  },
  {
    title: 'Report Date',
    key: 'date',
    render(text, report) {
      return (
        <span>
          <Text>{dayjs(report.reportDate).format('ll')}</Text>
        </span>
      )
    },
  },
]

const ADMIN_COLUMNS = [
  {
    title: 'Parsing',
    key: 'pdfExtraction',
    render(text, report) {
      return (
        <DataExtractionIcon
          extractionStatus={report.pdfExtraction?.status}
          showLabel={false}
        />
      )
    },
  },
]

const LOCALE = {
  emptyText: 'This cycle doesn’t have the reports ready yet',
}

function Reports({
    cycleId,
    practiceId,
    onPageChange,
    fetchReports,
    page = 1,
    filters = {},
    onSelect = () => {},
    selected = [],
    pendingFetch = false,
    ordersRequired = false,
  }) {
  const theme = useTheme()
  const classes = useStyles({ theme })
  const location = useLocation()

  useEffect(() => {
    sessionStorage.setItem('patientReturnTo', `${location.pathname}${location.search}`)
  }, [location.pathname, location.search])

  const reports = useReports(cycleId)
  const { total } = useReportsPagination(cycleId)
  const isAdmin = useIsAdmin()
  const isAcknowledged = useIsAcknowledged(cycleId)
  const [query, setConditions] = useQueryParams({})
  const canSign = useCanSign()
  const batchEnabled = useFeatureEnabled('report_batch_signatures')
  const rowSelection =
    canSign && batchEnabled
      ? {
          selectedRowKeys: selected,
          onChange: onSelect,
          getCheckboxProps: record => ({
            disabled: !!record.signedAt,
          }),
        }
      : null
  const [localStoragePageSize, setPageSize] = useLocalStorage(
    'pageSize',
    PAGE_SIZE,
  )
  const pageSize = query.perPage || localStoragePageSize

  useEffect(() => {
    fetchReports(practiceId, cycleId, page, filters, pageSize)
  }, [fetchReports, practiceId, cycleId, page, filters, pageSize])

  const shouldSign = useCallback(
    row =>
      row.hasBiobridgeReport && canSign && !row.signedAt && !row.isArchived,
    [canSign],
  )

  const rowClassName = useCallback(
    report => (report.active === false ? classes.inactiveRow : undefined),
    [classes.inactiveRow],
  )

  const columns = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'critical',
        key: 'critical',
        align: 'center',
        render(_text, { critical }) {
          return (
            critical && (
              <Tooltip title="Requires Attention">
                <ExclamationCircleOutlined className={classes.criticalIcon} />
              </Tooltip>
            )
          )
        },
      },
      {
        title: 'Patient',
        key: 'id',
        render: (_, row) => {
          if (!row?.patient) return null
          return (
            <PatientEntry patient={row.patient} />
          )
        },
      },
      ...BASE_COLUMNS,
      ...(isAdmin ? ADMIN_COLUMNS : []),
      ...(ordersRequired
        ? [
            {
              title: 'Order',
              dataIndex: 'hasOrder',
              key: 'hasOrder',
              align: 'center',
              render(_text, { hasOrder }) {
                return <CircleCheckIcon checked={hasOrder} />
              },
            },
          ]
        : []),
      {
        title: 'Signed',
        dataIndex: 'signed',
        key: 'signedAt',
        align: 'center',
        render(_text, { signedAt }) {
          return <CircleCheckIcon checked={signedAt} />
        },
      },
      {
        title: 'PDF',
        dataIndex: 'hasBiobridgeReport',
        key: 'hasBiobridgeReport',
        align: 'center',
        render(_text, { hasBiobridgeReport }) {
          return <CircleCheckIcon checked={hasBiobridgeReport} />
        },
      },
      {
        title: 'Full',
        dataIndex: 'hasDeviceReport',
        key: 'hasDeviceReport',
        align: 'center',
        render(_text, { hasDeviceReport }) {
          return <CircleCheckIcon checked={hasDeviceReport} />
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render(_text, report) {
          return (
            <div className={classes.actions}>
              {!report.hasDeviceReport && (
                <Link to={reportEditPath(report)}>
                  <Tooltip title="Add Device Report">
                    <Button type="link" icon={<PlusOutlined />} size="small" />
                  </Tooltip>
                </Link>
              )}
              {shouldSign(report) && (
                <Link to={reportShowPath(report)}>
                  <Tooltip title="Sign Report">
                    <Button type="link" icon={<EditOutlined />} size="small" />
                  </Tooltip>
                </Link>
              )}
              {report.hasDeviceReport && (
                <>
                  <Link to={reportShowPath(report)}>
                    <Tooltip title="View Report">
                      <Button type="link" icon={<FileSearchOutlined />} size="small" />
                    </Tooltip>
                  </Link>
                  <Link to={reportEditPath(report)}>
                    <Tooltip title="Edit Report">
                      <Button type="link" icon={<EditOutlined />} size="small" />
                    </Tooltip>
                  </Link>
                </>
              )}
            </div>
          )
        },
      },
    ],
    [classes, shouldSign, isAdmin, ordersRequired],
  )

  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      if (newPageSize !== Number(pageSize)) {
        setPageSize(newPageSize)
        setConditions({ ...query, cycleId, page: newPage, perPage: newPageSize })
      } else {
        onPageChange(newPage)
      }
    },
    [pageSize, query, cycleId, setConditions, setPageSize, onPageChange],
  )

  if (!pendingFetch && !isAdmin && !isAcknowledged && reports.length) {
    return <Acknowledge practiceId={practiceId} cycleId={cycleId} />
  }

  return (
    <>
      {isAdmin && (
        <div className={classes.parsingReference}>
          <DataExtractionReference />
        </div>
      )}

      <Table
        columns={columns}
        dataSource={reports}
        rowKey="id"
        loading={pendingFetch}
        locale={LOCALE}
        size="middle"
        rowSelection={rowSelection}
        rowClassName={rowClassName}
        pagination={{
          current: page,
          pageSize: Number(pageSize),
          onChange: handlePaginationChange,
          total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />
    </>
  )
}

Reports.propTypes = {
  cycleId: PropTypes.string.isRequired,
  practiceId: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  fetchReports: PropTypes.func.isRequired,
  pendingFetch: PropTypes.bool,
  page: PropTypes.number,
  filters: PropTypes.shape({}),
  onSelect: PropTypes.func,
  selected: PropTypes.arrayOf(PropTypes.string),
  ordersRequired: PropTypes.bool,
}

export default Reports
